import {FC, ReactNode, useContext, useEffect, useReducer} from 'react';

import {AppContext, AppMenuWidth, IAppContextState, INITIAL_STATE, NotificationSetType} from './AppContext.ts';
import {AppReducer} from './AppReducer.ts';
import {useWidth} from '@/hooks/useWidth.ts';
import {isWidthDown} from '@mui/material/Hidden/withWidth';
import {IAuthUserInfo} from '@/types/Auth.ts';
import {IRole} from '@/types/role';
import {IUser} from '@/types/user';
import {INotification} from '@/types/notification';

interface IAppProviderProps {
    children: ReactNode | ReactNode[]
}

export const AppProvider: FC<IAppProviderProps> = ({children}) => {
    const [{auth,wsClient, menu, layout, loaded, notifications, lastWsNotification, newTask, activeTasks}, dispatch] = useReducer(AppReducer, INITIAL_STATE);

    const width = useWidth()

    useEffect(() => {
        dispatch({type: 'setBreakPoint', payload: width});

        if (isWidthDown('sm', width)) {
            dispatch({type: 'setDevice', payload: 'mobile'});
        } else if (isWidthDown('md', width)) {
            dispatch({type: 'setDevice', payload: 'tablet'});

        } else {
            dispatch({type: 'setDevice', payload: 'pc'});

        }

    }, [width])


    const setAuthUserInfo = (value: IAuthUserInfo) => {
        dispatch({type: 'setAuthUserInfo', payload: value});
    };


    const setLoaded = (value: boolean) => {
        dispatch({type: 'setLoaded', payload: value});
    };

    const setMenuOpen = (value: boolean) => {
        dispatch({type: 'setMenuOpen', payload: value});
    };

    const setMenuWidth = (value: AppMenuWidth) => {
        dispatch({type: 'setMenuWidth', payload: value});
    };

    const cleanState = () => {
        dispatch({type: 'cleanState'});
    };

    const setAuthUserRoles = (value: IRole[]) => {
        dispatch({type: 'setAuthUserRoles', payload: value});
    };

    const setAuthUser = (value: IUser) => {
        dispatch({type: 'setAuthUser', payload: value});
    };

    const setNotifications = (values: INotification[], type: NotificationSetType = 'set') => {
        dispatch({type: 'setNotifications', payload: {values, type}});
    };

    const pushNotification = (value: INotification ) => {
        dispatch({type: 'pushNotification', payload: value});
    };

    const setWsClient = (values: WebSocket | null) => {
        dispatch({ type: 'setWsClient', payload:  values  });
    };

    const setNewTask = (values: boolean) => {
        dispatch({ type: 'setNewTask', payload:  values  });
    };

    const setActiveTask = (values: number) => {
        dispatch({ type: 'setActiveTask', payload:  values  });
    };


    return (
        <AppContext.Provider
            value={{
                auth,
                layout,
                menu,
                loaded,
                notifications,
                lastWsNotification,
                setLoaded,
                setMenuOpen,
                setMenuWidth,
                setAuthUserInfo,
                cleanState,
                setAuthUserRoles,
                setAuthUser,
                setNotifications,
                pushNotification,
                setWsClient,
                setActiveTask,
                wsClient,
                setNewTask,
                newTask,
                activeTasks
            }}
        >
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = (): IAppContextState => useContext(AppContext);
